import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  PublicClientApplication,
  AccountInfo,
  InteractionStatus,
  EventType,
  AuthenticationResult,
} from "@azure/msal-browser";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { fetchAuthConfig, createMsalConfig, loginRequest } from "./msalConfig";

// Microsoft Graph user profile
interface GraphProfile {
  mail: string | null;
  userPrincipalName: string | null;
  displayName: string | null;
}

// Auth context value interface
export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getEmail: () => string | null;
  getName: () => string | null;
  getUserId: () => string | null;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Inner provider that uses MSAL hooks
function AuthContextInner({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const [graphProfile, setGraphProfile] = useState<GraphProfile | null>(null);

  useEffect(() => {
    // Once MSAL is done with any interaction, we're no longer loading
    if (inProgress === InteractionStatus.None) {
      setIsLoading(false);
    }
  }, [inProgress]);

  const user = accounts.length > 0 ? accounts[0] : null;

  // Fetch user profile via backend (which uses client credentials to call Graph)
  useEffect(() => {
    async function fetchGraphProfile() {
      if (!user || !isAuthenticated) {
        setGraphProfile(null);
        return;
      }

      try {
        // Get the user's object ID from token claims or account info
        const claims = user.idTokenClaims as Record<string, unknown> | undefined;
        const oid = (claims?.oid as string) || user.localAccountId;
        
        if (!oid) {
          return;
        }

        // Call our backend which uses client credentials to call Graph
        const response = await fetch(`/api/auth/graph/profile?oid=${encodeURIComponent(oid)}`);
        
        if (response.ok) {
          const profile = await response.json();
          setGraphProfile({
            mail: profile.mail || null,
            userPrincipalName: profile.userPrincipalName || null,
            displayName: profile.displayName || null,
          });
        }
      } catch (error) {
        // Silent fail - will fall back to token claims
      }
    }

    fetchGraphProfile();
  }, [user, isAuthenticated]);

  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("Login failed:", error);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, [instance]);

  const getEmail = useCallback(() => {
    return graphProfile?.mail ?? null;
  }, [graphProfile]);

  const getName = useCallback(() => {
    if (!user) return null;
    const claims = user.idTokenClaims as Record<string, unknown> | undefined;
    return (
      graphProfile?.displayName ||
      (claims?.name as string) ||
      (claims?.given_name as string) ||
      user.name ||
      user.username ||
      null
    );
  }, [user, graphProfile]);

  const getUserId = useCallback(() => {
    if (!user) return null;
    // Get the user's object ID from token claims or account info
    const claims = user.idTokenClaims as Record<string, unknown> | undefined;
    return (claims?.oid as string) || user.localAccountId || null;
  }, [user]);

  const value: AuthContextValue = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    getEmail,
    getName,
    getUserId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Singleton to prevent multiple MSAL initializations (React Strict Mode runs effects twice)
let msalInitPromise: Promise<PublicClientApplication> | null = null;

async function getOrCreateMsalInstance(): Promise<PublicClientApplication> {
  if (msalInitPromise) {
    return msalInitPromise;
  }

  msalInitPromise = (async () => {
    // Fetch config from API
    const authConfig = await fetchAuthConfig();

    // Create MSAL instance
    const config = createMsalConfig(authConfig);
    const instance = new PublicClientApplication(config);

    // Initialize MSAL
    await instance.initialize();

    // Handle redirect response if any
    const response = await instance.handleRedirectPromise();
    if (response) {
      instance.setActiveAccount(response.account);
    } else {
      // If no redirect response, check for existing accounts
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        instance.setActiveAccount(accounts[0]);
      }
    }

    // Listen for login events
    instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        instance.setActiveAccount(payload.account);
      }
    });

    return instance;
  })();

  return msalInitPromise;
}

// Main provider that fetches config and initializes MSAL
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    getOrCreateMsalInstance()
      .then((instance) => {
        if (mounted) {
          setMsalInstance(instance);
        }
      })
      .catch((err) => {
        console.error("Failed to initialize MSAL:", err);
        if (mounted) {
          setError(err instanceof Error ? err.message : "Failed to initialize authentication");
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Show loading state while MSAL is initializing
  if (!msalInstance) {
    if (error) {
      return (
        <div style={{ padding: "2rem", color: "var(--message-error-fg)" }}>
          <p>Authentication Error: {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      );
    }
    return (
      <div style={{ padding: "2rem", display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: "var(--nav-bg)" }}>
        <p style={{ color: "var(--nav-fg)" }}>Initializing authentication...</p>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthContextInner>{children}</AuthContextInner>
    </MsalProvider>
  );
}

// Hook to use auth context
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
